import {
    INLINE_CHART_STYLE,
    InlineInspectionStyle,
    InlineReadout,
    InlineReadoutSpacer,
    squareActiveDot,
    useChartInspection
} from "@solomon-apps/shared/src/components/Charts/chartInspection";
import React, { useMemo } from "react";
import {
    CartesianGrid,
    Line,
    LineChart,
    ResponsiveContainer,
    Tooltip,
    XAxis,
    YAxis
} from "recharts";

import {
    formatLongDate,
    formatPct,
    formatShortDate
} from "../../shared/format";
import { niceTicks } from "../chartAxis";
import {
    hasChartHistory,
    type ReportingRange,
    windowByRange
} from "../chartVisibility";
import { graphColor } from "../graphColors";
import {
    type RateRow,
    reportValues,
    REWARDS_RATE_HINT,
    TOTAL_SLOT
} from "../reportingSeries";

import { ReportPanel, SparseRangeNote } from "./ReportPanel";

const TITLE = "Rewards Rate";
const AXIS_TICK = {
    fill: "rgb(var(--color-foreground-base) / 0.6)",
    fontSize: 11,
    fontFamily: "ABC Social"
};

export function describeRewardsRate(rows: readonly RateRow[]): string {
    const first = rows[0];
    const last = rows.filter((row) => row.total !== null).at(-1);
    if (!first || !last) return TITLE;
    return `${TITLE}, realized APY per settlement period, ${formatLongDate(first.t)} to ${formatLongDate(last.t)}. Latest ${formatPct(last.total!, 1)}.`;
}

/**
 * Realized APY per settlement period. A line, not an area: a rate is not an
 * accumulated quantity, and fill would say it is (data-visualization.md §2).
 * One series, so no legend; the hint carries the formula. Hover is the
 * standard inline inspection (`chartInspection`).
 */
export function RewardsRateReport({
    rows: allRows,
    range
}: {
    rows: RateRow[];
    range: ReportingRange;
}): React.ReactElement {
    const rows = useMemo(() => windowByRange(allRows, range), [allRows, range]);
    const inRange = hasChartHistory(reportValues(rows));
    const ticks = useMemo(
        () => niceTicks(Math.max(0, ...rows.map((row) => row.total ?? 0))),
        [rows]
    );
    const color = graphColor(TOTAL_SLOT);
    const inspection = useChartInspection();

    return (
        <ReportPanel title={TITLE} hint={REWARDS_RATE_HINT}>
            {inRange ? (
                <div
                    className="pt-4 pb-2 select-none"
                    {...inspection.containerProps}
                >
                    <InlineInspectionStyle />
                    <p className="sr-only">{describeRewardsRate(rows)}</p>
                    <InlineReadoutSpacer rows={1} />
                    <ResponsiveContainer width="100%" height={240} minWidth={0}>
                        <LineChart
                            data={rows}
                            margin={{ top: 8, right: 16, left: 8, bottom: 4 }}
                            style={INLINE_CHART_STYLE}
                        >
                            <CartesianGrid
                                horizontal
                                vertical={false}
                                stroke="rgb(var(--color-foreground-base) / 0.08)"
                            />
                            <XAxis
                                dataKey="t"
                                type="number"
                                scale="time"
                                domain={["dataMin", "dataMax"]}
                                tickFormatter={(v: number) =>
                                    formatShortDate(v)
                                }
                                axisLine={false}
                                tickLine={false}
                                tick={AXIS_TICK}
                                minTickGap={48}
                                padding={{ left: 8, right: 8 }}
                            />
                            <YAxis
                                domain={[0, ticks[ticks.length - 1]]}
                                ticks={ticks}
                                tickFormatter={(v: number) => formatPct(v, 1)}
                                axisLine={false}
                                tickLine={false}
                                width={56}
                                tick={AXIS_TICK}
                            />
                            <Tooltip
                                {...inspection.tooltipProps}
                                content={(props) => {
                                    const p = props as unknown as {
                                        active?: boolean;
                                        payload?: ReadonlyArray<{
                                            payload?: RateRow;
                                        }>;
                                    };
                                    const row = p.payload?.[0]?.payload;
                                    return (
                                        <InlineReadout
                                            active={Boolean(p.active)}
                                            x={props.coordinate?.x}
                                            rows={[
                                                {
                                                    key: "total",
                                                    name: "APY",
                                                    color,
                                                    value:
                                                        row !== undefined &&
                                                        row.total !== null
                                                            ? formatPct(
                                                                  row.total,
                                                                  1
                                                              )
                                                            : null
                                                }
                                            ]}
                                            date={
                                                row
                                                    ? formatLongDate(row.t)
                                                    : null
                                            }
                                            onActiveChange={
                                                inspection.setInspecting
                                            }
                                        />
                                    );
                                }}
                            />
                            <Line
                                type="linear"
                                connectNulls={false}
                                dataKey="total"
                                name="APY"
                                stroke={color}
                                strokeWidth={2}
                                dot={false}
                                activeDot={squareActiveDot(color)}
                                isAnimationActive
                                animationDuration={400}
                            />
                        </LineChart>
                    </ResponsiveContainer>
                </div>
            ) : (
                <SparseRangeNote />
            )}
        </ReportPanel>
    );
}
